import api from './api';

const authService = {
  /**
   * Register a new user with email and password
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.first_name - User first name
   * @param {string} userData.last_name - User last name
   * @returns {Promise<Object>} User data and tokens
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', {
      ...userData,
      anonymous_id: localStorage.getItem('op_anonymous_id')
    });
    return response.data;
  },

  /**
   * Register a new user through simplified enrollment flow
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.first_name - User first name
   * @param {string} userData.last_name - User last name
   * @param {string} [userData.phone_number] - User phone number (optional)
   * @returns {Promise<Object>} User data and tokens
   */
  simplifiedEnroll: async (userData) => {
    const response = await api.post('/auth/simplified-enroll', {
      ...userData,
      anonymous_id: localStorage.getItem('op_anonymous_id')
    });
    return response.data;
  },

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data and tokens
   */
  login: async (email, password) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      anonymous_id: localStorage.getItem('op_anonymous_id')
    });
    return response.data;
  },

  /**
   * Login with Google OAuth
   * @param {string} code - Google authorization code
   * @returns {Promise<Object>} User data and tokens
   */
  googleLogin: async (code) => {
    // Send the same redirect_uri that was used to get the code
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const response = await api.post('/auth/google', {
      code,
      redirect_uri: redirectUri,
      anonymous_id: localStorage.getItem('op_anonymous_id')
    });
    return response.data;
  },

  /**
   * Login with Google One-Tap
   * @param {string} idToken - Google ID token
   * @returns {Promise<Object>} User data and tokens
   */
  googleOneTapLogin: async (idToken) => {
    console.log('Sending Google ID token to backend:', idToken ? 'YES' : 'NO');
    try {
      const response = await api.post('/auth/google/one-tap', {
        id_token: idToken,
        anonymous_id: localStorage.getItem('op_anonymous_id')
      });
      console.log('Received response from backend:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in googleOneTapLogin:', error);
      throw error;
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Even if logout fails on backend, we still clear local storage
      console.error('Logout error:', error);
    }
  },

  /**
   * Refresh access token
   * @returns {Promise<Object>} New tokens
   */
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await api.post('/auth/refresh', {}, {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });
    return response.data;
  },

  /**
   * Verify email with token
   * @param {string} token - Email verification token
   * @returns {Promise<Object>} Verification result
   */
  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset request result
   */
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Reset result
   */
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User data
   */
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },
};

export default authService;